import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Download, Upload } from "lucide-react";

import { apiClient } from "@/api/client";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: string[];
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

async function downloadCsv(path: string, params: Record<string, string>, filename: string) {
  const r = await apiClient.get<Blob>(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ImportExportPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [from, setFrom] = useState(thirtyDaysAgoIso());
  const [to, setTo] = useState(todayIso());

  const isAdminPlus = me.data?.role === "admin" || me.data?.role === "super_admin";
  const isSuperAdmin = me.data?.role === "super_admin";

  if (!isAdminPlus) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">{t("common.read_only")}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("import_export.title")}</CardTitle>
          <CardDescription>{t("import_export.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>{t("audit.from")}</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[180px]" />
          </div>
          <div className="space-y-1">
            <Label>{t("audit.to")}</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[180px]" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ExportCard
          title={t("import_export.export_metrics_title")}
          description={t("import_export.export_metrics_desc")}
          path="/admin/export/metrics-hourly"
          filenamePrefix="metrics_hourly"
          from={from}
          to={to}
        />
        <ExportCard
          title={t("import_export.export_audit_title")}
          description={t("import_export.export_audit_desc")}
          path="/admin/export/audit"
          filenamePrefix="audit"
          from={from}
          to={to}
        />
      </div>

      {isSuperAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ImportCard
            title={t("import_export.import_cost_title")}
            description={t("import_export.import_cost_desc")}
            path="/admin/import/cost-parameters"
            schemaExample={`effective_month,code,name,value,unit,quantity,notes
2026-04-01,gas_price,Газ,12.5,грн/м³,1,
2026-04-01,rent,Оренда,50000,грн/місяць,1,`}
          />
          <ImportCard
            title={t("import_export.import_telemetry_title")}
            description={t("import_export.import_telemetry_desc")}
            path="/admin/import/telemetry-raw"
            schemaExample={`ts,source_id,metric,value,quality
2026-04-29T07:00:00+00:00,<source-uuid>,a_plus_kwh,884,good
2026-04-29T07:00:00+00:00,<source-uuid>,a_minus_kwh,0,good`}
          />
          <ImportCard
            title={t("import_export.import_plan_title")}
            description={t("import_export.import_plan_desc")}
            path="/admin/import/plan-hours"
            schemaExample={`plan_date,hour,power_kw,volumes
2026-04-30,7,1200,"{""rdn"":900,""ekosfera"":300}"
2026-04-30,8,1300,"{""rdn"":950,""ekosfera"":350}"`}
          />
        </div>
      )}
    </div>
  );
}

function ExportCard({
  title,
  description,
  path,
  filenamePrefix,
  from,
  to,
}: {
  title: string;
  description: string;
  path: string;
  filenamePrefix: string;
  from: string;
  to: string;
}) {
  const { t } = useTranslation();
  const dl = useMutation({
    mutationFn: () =>
      downloadCsv(path, { from, to }, `${filenamePrefix}_${from}_${to}.csv`),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => dl.mutate()} disabled={dl.isPending || from > to}>
          <Download className={cn("h-4 w-4", dl.isPending && "animate-pulse")} />
          {dl.isPending ? t("common.loading") : t("import_export.download")}
        </Button>
      </CardContent>
    </Card>
  );
}

function ImportCard({
  title,
  description,
  path,
  schemaExample,
}: {
  title: string;
  description: string;
  path: string;
  schemaExample: string;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<(ImportResult & { dry_run?: boolean }) | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async ({ file, dry_run }: { file: File; dry_run: boolean }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", String(dry_run));
      const r = await apiClient.post<ImportResult & { dry_run?: boolean }>(
        path, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: (r) => {
      setResult(r);
      setErr(null);
      if (!r.dry_run) {
        // Реальний імпорт — очищуємо файл і pending.
        if (fileRef.current) fileRef.current.value = "";
        setPendingFile(null);
      }
    },
    onError: () => {
      setErr(t("clients.errors.unknown"));
      setResult(null);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-lg bg-slate-50 p-3 text-xs font-mono whitespace-pre overflow-x-auto">
          {schemaExample}
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              setPendingFile(e.target.files?.[0] ?? null);
              setResult(null);
              setErr(null);
            }}
            className="text-sm"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => pendingFile && upload.mutate({ file: pendingFile, dry_run: true })}
            disabled={!pendingFile || upload.isPending}
          >
            {upload.isPending ? t("common.loading") : t("import_export.dry_run")}
          </Button>
          <Button
            onClick={() => pendingFile && upload.mutate({ file: pendingFile, dry_run: false })}
            disabled={!pendingFile || upload.isPending}
          >
            <Upload className="h-4 w-4" /> {t("import_export.upload")}
          </Button>
        </div>

        {result && (
          <div className={cn(
            "text-sm space-y-1 rounded-md p-3",
            result.dry_run ? "bg-blue-50 border border-blue-200" : "bg-emerald-50 border border-emerald-200",
          )}>
            <div className="font-medium">
              {result.dry_run ? `🔍 ${t("import_export.dry_run_label")}` : `✅ ${t("import_export.committed_label")}`}
            </div>
            <div>
              <span className="text-emerald-700 font-medium">+{result.inserted}</span>{" "}
              {t("import_export.inserted")},{" "}
              <span className="text-blue-700 font-medium">~{result.updated}</span>{" "}
              {t("import_export.updated")},{" "}
              <span className="text-slate-500">∅{result.skipped}</span>{" "}
              {t("import_export.skipped")}
            </div>
            {result.errors.length > 0 && (
              <details className="text-xs text-red-700">
                <summary className="cursor-pointer">
                  {t("import_export.errors")} ({result.errors.length})
                </summary>
                <ul className="list-disc pl-5 mt-1">
                  {result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {err && <div className="text-sm text-red-600">{err}</div>}
      </CardContent>
    </Card>
  );
}
